import { useState, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import { LOAN_PRODUCTS } from "@/lib/loanProducts";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowRight, ArrowLeft, Briefcase, GraduationCap, Sprout, Building2, BadgeCheck, Users } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import FileUpload from "@/components/FileUpload";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

const CUSTOMER_TYPES = [
  {
    key: "civil_servant",
    label: "Civil Servant / SSB Employee",
    icon: BadgeCheck,
    desc: "Government employee paid via SSB. Our lowest rates and fastest approvals.",
    risk: "Lower risk",
    defaultProduct: "civil_servant_loan",
  },
  {
    key: "salary_based",
    label: "Salaried Employee",
    icon: Briefcase,
    desc: "Working at a registered private company with regular salary.",
    risk: "Lower risk",
    defaultProduct: "salary_based_loan",
  },
  {
    key: "business",
    label: "Business Owner / SME",
    icon: Building2,
    desc: "You run a registered business, trading company or sole proprietorship.",
    risk: "Standard",
    defaultProduct: "business_loan",
  },
  {
    key: "agricultural",
    label: "Farmer",
    icon: Sprout,
    desc: "Active farming operation — crops, livestock or mixed.",
    risk: "Standard",
    defaultProduct: "agricultural_loan",
  },
  {
    key: "pensioner",
    label: "Pensioner",
    icon: Users,
    desc: "Receiving a regular pension from government or registered fund.",
    risk: "Lower risk",
    defaultProduct: "pensioner_loan",
  },
  {
    key: "other",
    label: "Other",
    icon: GraduationCap,
    desc: "Something else (e.g. school fees with multiple income sources).",
    risk: "Case-by-case",
    defaultProduct: "school_fees_loan",
  },
];

const empty = {
  customer_type: "",
  product: "",
  amount: 1000,
  term_months: 12,
  purpose: "",
  applicant_name: "",
  applicant_email: "",
  applicant_phone: "",
  national_id: "",
  date_of_birth: "",
  address: "",
  employer_name: "",
  employee_number: "",
  ministry_department: "",
  job_title: "",
  monthly_income: 0,
  years_employed: 0,
  manager_name: "",
  head_of_department: "",
  business_name: "",
  business_registration: "",
  business_sector: "",
  business_monthly_revenue: 0,
  next_of_kin_name: "",
  next_of_kin_phone: "",
  next_of_kin_relationship: "",
  bank_name: "",
  bank_account_number: "",
  // Documents
  documents: {
    national_id_doc: null,
    payslip: null,
    employer_letter: null,
    bank_statement: null,
    business_proof: null,
    offtake_contract: null,
  },
  // Collateral
  has_offtake_contract: false,
  offtake_buyer: "",
  collateral_description: "",
  collateral_value: 0,
  // Compliance
  credit_check_consent: false,
  whatsapp_consent: false,
};

export default function Apply() {
  const { user, ready, register } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(() => {
    const preselected = loc.state?.product;
    if (preselected) {
      const ct = CUSTOMER_TYPES.find((c) => c.defaultProduct === preselected);
      return { ...empty, product: preselected, customer_type: ct?.key || "" };
    }
    return empty;
  });
  const [submitting, setSubmitting] = useState(false);

  // Inline account creation (after customer-type step) for logged-out applicants
  const [account, setAccount] = useState({ full_name: "", email: "", phone: "", password: "", date_of_birth: "" });
  const [accountError, setAccountError] = useState("");
  const [registering, setRegistering] = useState(false);

  // Decide ONCE (when auth resolves) whether to show the inline account step,
  // and freeze it so registering mid-wizard doesn't shift step indices.
  // Logged-in users skip the step entirely.
  const [includeAccountStep, setIncludeAccountStep] = useState(false);
  const accountDecided = useRef(false);
  useEffect(() => {
    if (ready && !accountDecided.current) {
      accountDecided.current = true;
      setIncludeAccountStep(!user);
    }
  }, [ready, user]);

  // Pull the applicant's name / email / phone through from their account so the
  // application form is pre-populated (no re-typing). Runs once the user is known
  // (either already logged in, or right after registering mid-wizard).
  const prefilledRef = useRef(false);
  useEffect(() => {
    if (user && !prefilledRef.current) {
      prefilledRef.current = true;
      setForm((f) => ({
        ...f,
        applicant_name: f.applicant_name || user.full_name || "",
        applicant_email: f.applicant_email || user.email || "",
        applicant_phone: f.applicant_phone || user.phone || "",
      }));
    }
  }, [user]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  // Compute steps array based on customer type
  const isBusinessLike = form.customer_type === "business" || form.customer_type === "agricultural";
  const isEmployeeLike = ["civil_servant", "salary_based", "pensioner"].includes(form.customer_type);

  const steps = useMemo(() => {
    const base = ["Customer type"];
    if (includeAccountStep) base.push("Create account");
    base.push("Loan details", "Personal info");
    if (isEmployeeLike) base.push("Employment & income");
    if (isBusinessLike) base.push("Business details");
    if (form.customer_type === "other") base.push("Income");
    if (form.customer_type === "agricultural") base.push("Off-take & collateral");
    else base.push("Collateral (optional)");
    base.push("Documents");
    base.push("References & bank");
    base.push("Review & submit");
    return base;
  }, [form.customer_type, isEmployeeLike, isBusinessLike, includeAccountStep]);

  const progress = ((step + 1) / steps.length) * 100;

  const canProceed = () => {
    const stepName = steps[step];
    if (stepName === "Customer type") return Boolean(form.customer_type);
    if (stepName === "Create account") {
      if (user) return true;
      return Boolean(account.full_name.trim() && account.email.trim() && account.password.length >= 6);
    }
    if (stepName === "Loan details") return form.product && form.amount > 0 && form.term_months > 0 && form.purpose.length > 3;
    if (stepName === "Personal info") {
      return form.applicant_name && form.national_id && form.date_of_birth && form.address;
    }
    if (steps[step] === "Employment & income") {
      // All employed/pensioner applicants must provide SSB number, department, manager, HoD
      const baseOk = form.employee_number && form.ministry_department && form.manager_name && form.head_of_department && form.job_title && form.monthly_income > 0;
      if (!baseOk) return false;
      if (form.customer_type !== "civil_servant" && !form.employer_name) return false;
      return true;
    }
    if (steps[step] === "Business details") {
      return form.business_name && form.business_sector && form.business_monthly_revenue > 0;
    }
    if (steps[step] === "Income") {
      return form.monthly_income > 0;
    }
    if (steps[step] === "Off-take & collateral") {
      // For agriculture: require off-take answer (yes/no). If yes, require contract upload + buyer.
      if (form.has_offtake_contract) {
        return Boolean(form.offtake_buyer && form.documents?.offtake_contract);
      }
      return true; // 'No' is fine
    }
    if (steps[step] === "Collateral (optional)") {
      return true; // entirely optional
    }
    if (steps[step] === "Documents") {
      // Require ID for everyone; payslip for employee-like; business proof for business/agri.
      if (!form.documents?.national_id_doc) return false;
      if (isEmployeeLike && !form.documents?.payslip) return false;
      if (isBusinessLike && !form.documents?.business_proof) return false;
      return true;
    }
    if (steps[step] === "References & bank") {
      return form.next_of_kin_name && form.next_of_kin_phone && form.bank_name && form.bank_account_number;
    }
    return true;
  };

  const handleNext = async () => {
    if (!canProceed()) {
      toast.error("Please complete required fields.");
      return;
    }
    // Inline account creation step
    if (steps[step] === "Create account" && !user) {
      setRegistering(true);
      setAccountError("");
      const res = await register({
        full_name: account.full_name.trim(),
        email: account.email.trim(),
        phone: account.phone.trim() || undefined,
        password: account.password,
        date_of_birth: account.date_of_birth || undefined,
      });
      setRegistering(false);
      if (!res.ok) {
        setAccountError(res.error);
        toast.error(res.error);
        return;
      }
      toast.success("Account created — let's finish your application.");
    }
    setStep((s) => Math.min(s + 1, steps.length - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const handleBack = () => {
    setStep((s) => Math.max(s - 1, 0));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async () => {
    if (!user) {
      toast.error("Please create your account first.");
      return;
    }
    setSubmitting(true);
    try {
      // Flatten the documents map → list of { kind, file_id, original_filename }
      const docList = Object.entries(form.documents || {})
        .filter(([, v]) => v && v.id)
        .map(([k, v]) => ({
          kind: k,
          file_id: v.id,
          original_filename: v.original_filename,
          content_type: v.content_type,
        }));
      // Coerce numerics
      const payload = {
        ...form,
        amount: Number(form.amount),
        term_months: Number(form.term_months),
        monthly_income: Number(form.monthly_income),
        years_employed: form.years_employed ? Number(form.years_employed) : null,
        business_monthly_revenue: form.business_monthly_revenue ? Number(form.business_monthly_revenue) : null,
        collateral_value: form.collateral_value ? Number(form.collateral_value) : null,
        documents: docList,
      };
      await api.post("/applications", payload);
      toast.success("Application submitted!");
      navigate(`/dashboard/applications`, { replace: true });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const renderStep = () => {
    const stepName = steps[step];
    if (stepName === "Customer type") {
      return (
        <div>
          <h2 className="font-heading text-2xl font-semibold text-ink">Which best describes you?</h2>
          <p className="mt-2 text-sm text-ink-soft">Your answer routes you to the most suitable product and risk path.</p>
          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            {CUSTOMER_TYPES.map((c) => {
              const Icon = c.icon;
              const active = form.customer_type === c.key;
              return (
                <button
                  type="button"
                  key={c.key}
                  data-testid={`customer-type-${c.key}`}
                  onClick={() => set({ customer_type: c.key, product: c.defaultProduct })}
                  className={`flex items-start gap-4 rounded-xl border p-4 text-left transition-all ${
                    active ? "border-[#5B2C82] bg-[#F9F6F0] ring-2 ring-[#5B2C82]/30" : "border-[#E5E2DE] bg-white hover:border-[#5B2C82]"
                  }`}
                >
                  <div className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${active ? "bg-[#5B2C82] text-white" : "bg-[#F9F6F0] text-[#5B2C82]"}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <div className="font-heading text-base font-semibold text-ink">{c.label}</div>
                    <p className="mt-1 text-xs text-ink-soft">{c.desc}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      );
    }
    if (stepName === "Create account") {
      if (user) {
        return (
          <div className="space-y-4">
            <h2 className="font-heading text-2xl font-semibold text-ink">You&apos;re signed in</h2>
            <div className="flex items-start gap-3 rounded-xl border border-emerald-300 bg-emerald-50 p-4 text-sm text-ink" data-testid="apply-account-signedin">
              <BadgeCheck className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
              <div>
                Signed in as <span className="font-semibold">{user.full_name}</span> ({user.email}).
                Your application is being saved to this account — continue below.
              </div>
            </div>
          </div>
        );
      }
      return (
        <div className="space-y-5">
          <h2 className="font-heading text-2xl font-semibold text-ink">Create your account</h2>
          <p className="text-sm text-ink-soft">
            We&apos;ll save your progress so you can track this application and your repayments later.
            It takes less than a minute.
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label htmlFor="acc-name">Full name</Label>
              <Input id="acc-name" value={account.full_name} onChange={(e) => setAccount({ ...account, full_name: e.target.value })} className="mt-1.5" data-testid="apply-account-name" />
            </div>
            <div>
              <Label htmlFor="acc-email">Email</Label>
              <Input id="acc-email" type="email" value={account.email} onChange={(e) => setAccount({ ...account, email: e.target.value })} className="mt-1.5" data-testid="apply-account-email" />
            </div>
            <div>
              <Label htmlFor="acc-phone">Phone <span className="text-ink-muted">(optional)</span></Label>
              <Input id="acc-phone" value={account.phone} onChange={(e) => setAccount({ ...account, phone: e.target.value })} className="mt-1.5" data-testid="apply-account-phone" />
            </div>
            <div>
              <Label htmlFor="acc-dob">Date of birth <span className="text-ink-muted">(optional)</span></Label>
              <Input id="acc-dob" type="date" value={account.date_of_birth} onChange={(e) => setAccount({ ...account, date_of_birth: e.target.value })} className="mt-1.5" data-testid="apply-account-dob" />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="acc-password">Password</Label>
              <Input id="acc-password" type="password" minLength={6} value={account.password} onChange={(e) => setAccount({ ...account, password: e.target.value })} placeholder="At least 6 characters" className="mt-1.5" data-testid="apply-account-password" />
            </div>
          </div>
          {accountError && (
            <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="apply-account-error">
              {accountError}{" "}
              {String(accountError).toLowerCase().includes("registered") && (
                <Link to="/login" state={{ from: "/apply" }} className="font-semibold underline">Sign in instead</Link>
              )}
            </div>
          )}
          <p className="text-xs text-ink-muted">
            Already have an account?{" "}
            <Link to="/login" state={{ from: "/apply" }} className="font-semibold text-[#5B2C82] hover:text-[#E87722]" data-testid="apply-account-login-link">Sign in</Link>
          </p>
        </div>
      );
    }
    if (stepName === "Loan details") {
      const recommendedProduct = LOAN_PRODUCTS.find((p) => p.key === form.product);
      return (
        <div className="space-y-5">
          <h2 className="font-heading text-2xl font-semibold text-ink">Loan details</h2>
          <div>
            <Label>Product</Label>
            <Select value={form.product} onValueChange={(v) => set({ product: v })}>
              <SelectTrigger className="mt-1.5" data-testid="apply-product"><SelectValue placeholder="Select product" /></SelectTrigger>
              <SelectContent>
                {LOAN_PRODUCTS.map((p) => (
                  <SelectItem key={p.key} value={p.key}>{p.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {recommendedProduct && (
              <p className="mt-1.5 text-xs text-ink-muted">
                Range: ${recommendedProduct.minAmount.toLocaleString()}–${recommendedProduct.maxAmount.toLocaleString()} · {recommendedProduct.termRange} · {recommendedProduct.rateRange}
              </p>
            )}
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label>Amount (USD)</Label>
              <Input type="number" min="0" value={form.amount} onChange={(e) => set({ amount: e.target.value })} className="mt-1.5" data-testid="apply-amount" />
            </div>
            <div>
              <Label>Term (months)</Label>
              <Input type="number" min="1" max="60" value={form.term_months} onChange={(e) => set({ term_months: e.target.value })} className="mt-1.5" data-testid="apply-term" />
            </div>
          </div>
          <div>
            <Label>Purpose</Label>
            <Textarea rows={3} value={form.purpose} onChange={(e) => set({ purpose: e.target.value })} placeholder="What will this loan be used for?" className="mt-1.5" data-testid="apply-purpose" />
          </div>
        </div>
      );
    }
    if (stepName === "Personal info") {
      return (
        <div className="space-y-5">
          <h2 className="font-heading text-2xl font-semibold text-ink">Personal information</h2>
          <div className="rounded-xl border border-[#E5E2DE] bg-[#F9F6F0] p-4">
            <div className="text-xs uppercase tracking-wider text-ink-muted">From your account</div>
            <div className="mt-3 grid gap-4 sm:grid-cols-2">
              <div>
                <Label>Full name</Label>
                <Input value={form.applicant_name} onChange={(e) => set({ applicant_name: e.target.value })} className="mt-1.5 bg-white" data-testid="apply-applicant-name" />
              </div>
              <div>
                <Label>Email <span className="text-ink-muted">(account login)</span></Label>
                <Input value={form.applicant_email} readOnly className="mt-1.5 bg-[#EFECE6] text-ink-soft" data-testid="apply-applicant-email" />
              </div>
              <div className="sm:col-span-2">
                <Label>Phone</Label>
                <Input value={form.applicant_phone} onChange={(e) => set({ applicant_phone: e.target.value })} placeholder="e.g. +263 77 000 0000" className="mt-1.5 bg-white" data-testid="apply-applicant-phone" />
              </div>
            </div>
            <p className="mt-2 text-xs text-ink-muted">Pre-filled from your account — edit here if anything has changed.</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label>National ID</Label>
              <Input value={form.national_id} onChange={(e) => set({ national_id: e.target.value })} className="mt-1.5" data-testid="apply-natid" />
            </div>
            <div>
              <Label>Date of birth</Label>
              <Input type="date" value={form.date_of_birth} onChange={(e) => set({ date_of_birth: e.target.value })} className="mt-1.5" data-testid="apply-dob" />
            </div>
          </div>
          <div>
            <Label>Residential address</Label>
            <Textarea rows={2} value={form.address} onChange={(e) => set({ address: e.target.value })} className="mt-1.5" data-testid="apply-address" />
          </div>
        </div>
      );
    }
    if (stepName === "Employment & income") {
      return (
        <div className="space-y-5">
          <h2 className="font-heading text-2xl font-semibold text-ink">Employment & income</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label>{form.customer_type === "civil_servant" ? "Ministry" : "Employer name"}</Label>
              <Input
                value={form.customer_type === "civil_servant" ? "Government of Zimbabwe" : form.employer_name}
                readOnly={form.customer_type === "civil_servant"}
                onChange={(e) => set({ employer_name: e.target.value })}
                className="mt-1.5"
                data-testid="apply-employer"
              />
            </div>
            <div>
              <Label>SSB / Employee number</Label>
              <Input
                value={form.employee_number}
                onChange={(e) => set({ employee_number: e.target.value })}
                placeholder="e.g. EC12345 or SSB-987654"
                className="mt-1.5"
                data-testid="apply-empno"
              />
            </div>
            <div>
              <Label>Department / Unit</Label>
              <Input
                value={form.ministry_department}
                onChange={(e) => set({ ministry_department: e.target.value })}
                placeholder={form.customer_type === "civil_servant" ? "e.g. Ministry of Health, Outpatients" : "e.g. Finance, Operations"}
                className="mt-1.5"
                data-testid="apply-department"
              />
            </div>
            <div>
              <Label>Job title</Label>
              <Input value={form.job_title} onChange={(e) => set({ job_title: e.target.value })} className="mt-1.5" data-testid="apply-jobtitle" />
            </div>
            <div>
              <Label>Direct manager / supervisor</Label>
              <Input
                value={form.manager_name}
                onChange={(e) => set({ manager_name: e.target.value })}
                placeholder="Full name"
                className="mt-1.5"
                data-testid="apply-manager"
              />
            </div>
            <div>
              <Label>Head of Department (HoD)</Label>
              <Input
                value={form.head_of_department}
                onChange={(e) => set({ head_of_department: e.target.value })}
                placeholder="Full name"
                className="mt-1.5"
                data-testid="apply-hod"
              />
            </div>
            <div>
              <Label>Net monthly income (USD)</Label>
              <Input type="number" min="0" value={form.monthly_income} onChange={(e) => set({ monthly_income: e.target.value })} className="mt-1.5" data-testid="apply-income" />
            </div>
            <div>
              <Label>Years employed</Label>
              <Input type="number" min="0" step="0.5" value={form.years_employed} onChange={(e) => set({ years_employed: e.target.value })} className="mt-1.5" data-testid="apply-tenure" />
            </div>
          </div>
        </div>
      );
    }
    if (stepName === "Business details") {
      return (
        <div className="space-y-5">
          <h2 className="font-heading text-2xl font-semibold text-ink">Business details</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label>Business name</Label>
              <Input value={form.business_name} onChange={(e) => set({ business_name: e.target.value })} className="mt-1.5" data-testid="apply-bizname" />
            </div>
            <div>
              <Label>Registration number (if any)</Label>
              <Input value={form.business_registration} onChange={(e) => set({ business_registration: e.target.value })} className="mt-1.5" data-testid="apply-bizreg" />
            </div>
            <div>
              <Label>Sector</Label>
              <Select value={form.business_sector} onValueChange={(v) => set({ business_sector: v })}>
                <SelectTrigger className="mt-1.5" data-testid="apply-sector"><SelectValue placeholder="Select sector" /></SelectTrigger>
                <SelectContent>
                  {["Retail", "Wholesale", "Manufacturing", "Agriculture", "Services", "Transport", "Construction", "Mining", "Other"].map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Average monthly revenue (USD)</Label>
              <Input type="number" min="0" value={form.business_monthly_revenue} onChange={(e) => set({ business_monthly_revenue: e.target.value, monthly_income: e.target.value })} className="mt-1.5" data-testid="apply-bizrev" />
            </div>
          </div>
        </div>
      );
    }
    if (stepName === "Income") {
      return (
        <div className="space-y-5">
          <h2 className="font-heading text-2xl font-semibold text-ink">Income</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label>Source of income</Label>
              <Input value={form.job_title} onChange={(e) => set({ job_title: e.target.value, employer_name: e.target.value })} className="mt-1.5" data-testid="apply-source" />
            </div>
            <div>
              <Label>Monthly income (USD)</Label>
              <Input type="number" min="0" value={form.monthly_income} onChange={(e) => set({ monthly_income: e.target.value })} className="mt-1.5" data-testid="apply-income2" />
            </div>
          </div>
        </div>
      );
    }
    if (stepName === "Off-take & collateral") {
      const setDoc = (key, v) => set({ documents: { ...form.documents, [key]: v } });
      return (
        <div className="space-y-5">
          <h2 className="font-heading text-2xl font-semibold text-ink">Off-take contract & collateral</h2>
          <p className="text-sm text-ink-soft">
            An off-take contract from a buyer (e.g., a milling company, exporter or contractor)
            can serve as security and unlocks more favourable terms.
          </p>

          <div className="rounded-xl border border-[#E5E2DE] bg-white p-4">
            <Label>Is your farming venture supported by an off-take contract?</Label>
            <RadioGroup
              value={form.has_offtake_contract ? "yes" : "no"}
              onValueChange={(v) => set({ has_offtake_contract: v === "yes" })}
              className="mt-3 grid grid-cols-2 gap-3"
            >
              <label className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 ${form.has_offtake_contract ? "border-[#5B2C82] bg-[#F9F6F0]" : "border-[#E5E2DE]"}`}>
                <RadioGroupItem value="yes" data-testid="offtake-yes" /> <span className="text-sm">Yes, I have one</span>
              </label>
              <label className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 ${!form.has_offtake_contract ? "border-[#5B2C82] bg-[#F9F6F0]" : "border-[#E5E2DE]"}`}>
                <RadioGroupItem value="no" data-testid="offtake-no" /> <span className="text-sm">No, not at this time</span>
              </label>
            </RadioGroup>
          </div>

          {form.has_offtake_contract && (
            <div className="space-y-4">
              <div>
                <Label>Off-take buyer / counterparty</Label>
                <Input
                  value={form.offtake_buyer}
                  onChange={(e) => set({ offtake_buyer: e.target.value })}
                  placeholder="e.g. Delta Beverages, COTTCO, Tongaat Hulett"
                  className="mt-1.5"
                  data-testid="offtake-buyer"
                />
              </div>
              <FileUpload
                label="Off-take contract"
                hint="Signed contract or letter of commitment (PDF, JPG, PNG · max 10 MB)"
                kind="offtake_contract"
                value={form.documents.offtake_contract}
                onChange={(v) => setDoc("offtake_contract", v)}
                testId="upload-offtake"
              />
            </div>
          )}

          <div className="rounded-xl border border-[#E5E2DE] bg-white p-4">
            <div className="font-heading text-sm font-semibold text-ink">Additional collateral <span className="font-normal text-ink-muted">(optional)</span></div>
            <p className="mt-1 text-xs text-ink-muted">Equipment, livestock, vehicles or property pledged as security.</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Label>Description</Label>
                <Textarea
                  rows={2}
                  value={form.collateral_description}
                  onChange={(e) => set({ collateral_description: e.target.value })}
                  placeholder="e.g. 1× John Deere 5050D tractor, 20 cattle"
                  className="mt-1.5"
                  data-testid="collateral-desc"
                />
              </div>
              <div>
                <Label>Estimated value (USD)</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.collateral_value}
                  onChange={(e) => set({ collateral_value: e.target.value })}
                  className="mt-1.5"
                  data-testid="collateral-value"
                />
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (stepName === "Collateral (optional)") {
      return (
        <div className="space-y-5">
          <h2 className="font-heading text-2xl font-semibold text-ink">Collateral <span className="text-sm font-normal text-ink-muted">(optional)</span></h2>
          <p className="text-sm text-ink-soft">
            If you have an asset you&apos;d like to offer as security (e.g., for larger loans), tell us about it. Skip this step otherwise.
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label>Description of collateral</Label>
              <Textarea
                rows={2}
                value={form.collateral_description}
                onChange={(e) => set({ collateral_description: e.target.value })}
                className="mt-1.5"
                data-testid="collateral-desc"
                placeholder="e.g. Toyota Hilux 2018 reg. ABC1234; title deed Stand 1234 Borrowdale"
              />
            </div>
            <div>
              <Label>Estimated value (USD)</Label>
              <Input
                type="number"
                min="0"
                value={form.collateral_value}
                onChange={(e) => set({ collateral_value: e.target.value })}
                className="mt-1.5"
                data-testid="collateral-value"
              />
            </div>
          </div>
        </div>
      );
    }

    if (stepName === "Documents") {
      const setDoc = (key, v) => set({ documents: { ...form.documents, [key]: v } });
      return (
        <div className="space-y-5">
          <h2 className="font-heading text-2xl font-semibold text-ink">Documents</h2>
          <p className="text-sm text-ink-soft">
            Upload supporting documents. JPG, PNG or PDF up to 10 MB each. We treat your
            documents confidentially.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <FileUpload
              label="National ID"
              hint="Front of ID (required)"
              kind="national_id_doc"
              value={form.documents.national_id_doc}
              onChange={(v) => setDoc("national_id_doc", v)}
              testId="upload-natid"
            />
            {isEmployeeLike && (
              <FileUpload
                label="Recent payslip"
                hint="Most recent payslip (required)"
                kind="payslip"
                value={form.documents.payslip}
                onChange={(v) => setDoc("payslip", v)}
                testId="upload-payslip"
              />
            )}
            {isEmployeeLike && (
              <FileUpload
                label="Employer letter"
                hint="Confirmation of employment letter (recommended)"
                kind="employer_letter"
                value={form.documents.employer_letter}
                onChange={(v) => setDoc("employer_letter", v)}
                testId="upload-empletter"
              />
            )}
            {isBusinessLike && (
              <FileUpload
                label="Business proof"
                hint="Registration certificate or trading licence (required)"
                kind="business_proof"
                value={form.documents.business_proof}
                onChange={(v) => setDoc("business_proof", v)}
                testId="upload-bizproof"
              />
            )}
            <FileUpload
              label="Bank statement"
              hint="Last 3 months (recommended)"
              kind="bank_statement"
              value={form.documents.bank_statement}
              onChange={(v) => setDoc("bank_statement", v)}
              testId="upload-bankstmt"
            />
          </div>
        </div>
      );
    }

    if (stepName === "References & bank") {
      return (
        <div className="space-y-5">
          <h2 className="font-heading text-2xl font-semibold text-ink">References & bank</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label>Next of kin name</Label>
              <Input value={form.next_of_kin_name} onChange={(e) => set({ next_of_kin_name: e.target.value })} className="mt-1.5" data-testid="apply-kin-name" />
            </div>
            <div>
              <Label>Next of kin phone</Label>
              <Input value={form.next_of_kin_phone} onChange={(e) => set({ next_of_kin_phone: e.target.value })} className="mt-1.5" data-testid="apply-kin-phone" />
            </div>
            <div>
              <Label>Relationship</Label>
              <Input value={form.next_of_kin_relationship} onChange={(e) => set({ next_of_kin_relationship: e.target.value })} className="mt-1.5" data-testid="apply-kin-rel" />
            </div>
            <div>
              <Label>Bank</Label>
              <Input value={form.bank_name} onChange={(e) => set({ bank_name: e.target.value })} className="mt-1.5" data-testid="apply-bank" />
            </div>
            <div className="sm:col-span-2">
              <Label>Bank account number</Label>
              <Input value={form.bank_account_number} onChange={(e) => set({ bank_account_number: e.target.value })} className="mt-1.5" data-testid="apply-bankacc" />
            </div>
          </div>
        </div>
      );
    }
    if (stepName === "Review & submit") {
      const product = LOAN_PRODUCTS.find((p) => p.key === form.product);
      const uploadedDocs = Object.entries(form.documents || {})
        .filter(([, v]) => v && v.id);
      return (
        <div className="space-y-5">
          <h2 className="font-heading text-2xl font-semibold text-ink">Review & submit</h2>
          <div className="rounded-xl border border-[#E5E2DE] bg-[#F9F6F0] p-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <Row label="Full name" value={form.applicant_name} />
              <Row label="Phone" value={form.applicant_phone} />
              <Row label="Customer type" value={CUSTOMER_TYPES.find((c) => c.key === form.customer_type)?.label} />
              <Row label="Product" value={product?.title} />
              <Row label="Amount" value={`$${Number(form.amount).toLocaleString()}`} />
              <Row label="Term" value={`${form.term_months} months`} />
              <Row label="Purpose" value={form.purpose} full />
              <Row label="National ID" value={form.national_id} />
              <Row label="Date of birth" value={form.date_of_birth} />
              <Row label="Monthly income" value={`$${Number(form.monthly_income).toLocaleString()}`} />
              {(form.employee_number || form.manager_name) && (
                <>
                  <Row label="SSB / Employee #" value={form.employee_number} />
                  <Row label="Department" value={form.ministry_department} />
                  <Row label="Manager" value={form.manager_name} />
                  <Row label="Head of Department" value={form.head_of_department} />
                </>
              )}
              <Row label="Bank (disbursement)" value={`${form.bank_name} · ${form.bank_account_number}`} full />
              {form.customer_type === "agricultural" && (
                <Row
                  label="Off-take contract"
                  value={form.has_offtake_contract ? `Yes — ${form.offtake_buyer || "buyer pending"}` : "No"}
                  full
                />
              )}
              {(form.collateral_description || form.collateral_value > 0) && (
                <Row
                  label="Collateral"
                  value={`${form.collateral_description || "—"}${form.collateral_value ? ` · ~$${Number(form.collateral_value).toLocaleString()}` : ""}`}
                  full
                />
              )}
            </div>

            {uploadedDocs.length > 0 && (
              <div className="mt-5 border-t border-[#E5E2DE] pt-4">
                <div className="text-xs uppercase tracking-wider text-ink-muted">Uploaded documents</div>
                <ul className="mt-2 space-y-1 text-sm text-ink">
                  {uploadedDocs.map(([k, v]) => (
                    <li key={k} className="flex items-center justify-between" data-testid={`review-doc-${k}`}>
                      <span className="capitalize text-ink-soft">{k.replace(/_/g, " ")}</span>
                      <span className="ml-3 truncate font-medium">{v.original_filename}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Consent — must be ticked */}
          <label
            className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 ${
              form.credit_check_consent
                ? "border-[#5B2C82] bg-[#F9F6F0]"
                : "border-[#E87722]/60 bg-[#E87722]/5"
            }`}
            data-testid="apply-consent-label"
          >
            <input
              type="checkbox"
              checked={form.credit_check_consent}
              onChange={(e) => set({ credit_check_consent: e.target.checked })}
              className="mt-1 h-4 w-4 accent-[#5B2C82]"
              data-testid="apply-consent-checkbox"
            />
            <div className="text-sm leading-relaxed text-ink">
              <span className="font-heading font-semibold">Credit reference consent.</span>{" "}
              I authorise Joseph Roberts Capital to make enquiries with any registered credit
              reference bureau in Zimbabwe regarding my credit history, and to submit
              information about this application and any resulting facility to such bureaus.
              I confirm the information I have provided is true and complete to the best of
              my knowledge.
            </div>
          </label>

          {/* WhatsApp opt-in — optional */}
          <label
            className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 ${
              form.whatsapp_consent ? "border-emerald-400 bg-emerald-50" : "border-[#E5E2DE] bg-white"
            }`}
            data-testid="apply-whatsapp-label"
          >
            <input
              type="checkbox"
              checked={form.whatsapp_consent}
              onChange={(e) => set({ whatsapp_consent: e.target.checked })}
              className="mt-1 h-4 w-4 accent-emerald-600"
              data-testid="apply-whatsapp-checkbox"
            />
            <div className="text-sm leading-relaxed text-ink">
              <span className="font-heading font-semibold">WhatsApp updates <span className="text-ink-muted">(optional)</span>.</span>{" "}
              I&apos;m happy to be contacted on WhatsApp for application updates, repayment
              reminders and quick service messages. I can opt out at any time by replying STOP.
            </div>
          </label>
        </div>
      );
    }
    return null;
  };

  return (
    <PublicLayout>
      <section className="py-10 sm:py-16">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <div className="mb-3 flex items-center justify-between">
            <Link to="/products" className="text-sm text-ink-soft hover:text-[#5B2C82]" data-testid="apply-back-products">← Back to products</Link>
            <span className="text-xs uppercase tracking-wider text-ink-muted">Step {step + 1} of {steps.length}</span>
          </div>
          <Progress value={progress} className="h-1.5" data-testid="apply-progress" />
          <div className="mt-2 text-xs font-semibold uppercase tracking-[0.15em] text-[#E87722]" data-testid="apply-step-name">{steps[step]}</div>

          <div className="mt-6 rounded-2xl border border-[#E5E2DE] bg-white p-6 sm:p-8" data-testid="apply-wizard">
            {renderStep()}

            <div className="mt-8 flex items-center justify-between border-t border-[#E5E2DE] pt-5">
              <Button
                type="button"
                variant="ghost"
                onClick={handleBack}
                disabled={step === 0}
                className="text-ink-soft"
                data-testid="apply-back"
              >
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              {step < steps.length - 1 ? (
                <Button onClick={handleNext} disabled={!canProceed() || registering} className="bg-[#5B2C82] text-white hover:bg-[#46225f] disabled:opacity-50" data-testid="apply-next">
                  {registering && steps[step] === "Create account" ? (
                    "Creating account…"
                  ) : (
                    <>Continue <ArrowRight className="ml-2 h-4 w-4" /></>
                  )}
                </Button>
              ) : (
                <Button onClick={handleSubmit} disabled={submitting || !form.credit_check_consent} className="bg-[#E87722] text-white hover:bg-[#cc6517] disabled:opacity-50" data-testid="apply-submit">
                  {submitting ? "Submitting…" : "Submit application"}
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}

function Row({ label, value, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <div className="text-xs uppercase tracking-wider text-ink-muted">{label}</div>
      <div className="mt-0.5 text-sm font-medium text-ink">{value || "—"}</div>
    </div>
  );
}
